/** State of the merchant agreement, as returned by GET organizations/{id}/merchants (agreementStatus). */
export enum MerchantAgreementStatus {
    /** No agreement has been sent for signing. */
    NotSent = 'NotSent',
    /** The agreement is in Scrive, awaiting the signatories. */
    Pending = 'Pending',
    /** Every signatory has signed in Scrive. */
    Signed = 'Signed',
    /** Signed outside Merchant Hub before the merchant was added. */
    AlreadySigned = 'AlreadySigned',
    /** Rejected, cancelled or timed out in Scrive; a new agreement can be sent. */
    Failed = 'Failed',
}

/**
 * A person who may sign for the merchant according to the register, as returned by
 * GET organizations/{id}/merchants/{countryCode}/{organizationNumber}/agreements/signatories.
 */
export interface RegisteredSignatory {
    /** As the register writes it; Swedish registers list "LAST, FIRST". */
    fullName: string;
    /** Masked personal ID number, or null where the register has none (Finland). */
    personalId: string | null;
}

/** A signing party of the agreement, in the body of POST organizations/{id}/merchants/{countryCode}/{organizationNumber}/agreements. */
export interface AgreementSignatory {
    name: string;
    email: string;
    /** The masked id from the signatories endpoint; only a person with one can be picked to sign. */
    personalId?: string;
}

export interface SendAgreementRequest {
    signatories: AgreementSignatory[];
}

/** "ANNA-KARIN LINDQVIST" as "Anna-Karin Lindqvist": the first letter of each word and hyphenated part up, the rest down. */
const titleCase = (name: string): string =>
    name.toLowerCase().replace(/(^|[\s-])\p{L}/gu, match => match.toUpperCase());

/**
 * The name as people write it. A Swedish register lists "LINDQVIST, ANNA", which becomes "Anna Lindqvist": the first
 * name first, and in title case when the register shouts it. A name in any other shape is kept as it is.
 */
export const signatoryDisplayName = (fullName: string): string => {
    const [last, first, ...rest] = fullName.split(',').map(part => part.trim());
    const name = !first || rest.length > 0 ? fullName.trim() : `${first} ${last}`;

    return name === name.toUpperCase() ? titleCase(name) : name;
};
